// src/hooks/useOwnerAction.ts — real async owner write actions via ownerService.
import { useOwnerStore } from '@/core/stores/ownerStore';
import { useSession } from '@/hooks/useSession';
import { useToast } from '@/ui';
import { ownerService } from '@/core/services/ownerService';
import { ApiError } from '@/core/lib/apiClient';
import type { ModerationCategory, TopicRefinerRun } from '@/core/models';

/**
 * Turn a failed Council-office grant into something that teaches the next step.
 *
 * `self_action` is the refusal people meet FIRST. The founder is the only
 * superuser, so their own row sits in the Roles table showing "Seat as
 * President", and clicking it is the natural first thing anyone does. The
 * server refuses on purpose — nobody appoints themselves to the body that
 * judges everyone else — but a bare "Failed to update" makes a deliberate rule
 * look like a broken button and leaves the operator with nowhere to go. The
 * console command IS the supported path, so name it at the moment it's needed.
 *
 * (Hiding the button on your own row would "fix" the confusion by removing the
 * control without ever stating why, which is the defect this project keeps
 * having to undo.)
 */
function officeFailure(
  e: unknown,
  username: string,
  label: string,
  cliSlug: string,
  fallback: string,
): string {
  if (e instanceof ApiError && e.code === 'self_action') {
    return `You cannot appoint yourself ${label}. The Council does not allow self-appointment. Seat it from the server console: manage.py seat_council_office ${username} ${cliSlug}`;
  }
  if (e instanceof ApiError && e.code === 'office_occupied') {
    const incumbent = typeof e.fields?.incumbent === 'string' ? e.fields.incumbent : 'someone else';
    return `${label} is already held by ${incumbent}. Revoke it from them first. Handover is an explicit revoke-then-grant.`;
  }
  // A sanctioned account cannot hold an office: suspending or banning someone
  // strips all three, so seating one would reopen that hole from the other side.
  if (e instanceof ApiError && e.code === 'target_not_active') {
    return `${username} is suspended or banned, so they cannot be seated as ${label}. Lift the sanction first.`;
  }
  // There is still deliberately NO `not_an_appointer` branch here. Only the
  // three office setters call this, and none of their endpoints can return that
  // code. It would also name the wrong person: `username` here is the account
  // being granted an office, while `not_an_appointer` is a refusal about the
  // REQUESTER. The head-appointment endpoints — which DO return it, and which
  // the Council Teams section now calls — are translated by
  // `appointmentFailure` below, precisely so the two never share a `username`.
  return fallback;
}

/**
 * Which of the two head-moderator endpoints this failure came back from.
 *
 * One mapper serves both, and the two share four codes — so without this every
 * message would be written for the grant path and would then describe the wrong
 * action half the time. The reachable version of that: an operator clicks
 * "Remove head", the list is seconds stale because the Council decided the case
 * in between, and the toast tells them about an appointment they never made.
 */
type AppointmentAction = 'appoint' | 'remove';

/**
 * Turn a failed head-moderator appointment or removal into something that
 * teaches the next step. A separate translator from `officeFailure` on purpose.
 *
 * Every refusal these two endpoints return ships a carefully-written `detail`
 * sentence, and `ApiError` throws all of it away — it carries `code`, `status`
 * and `fields` only. So each message is re-authored here, and each one has to
 * stay true to the rule it reports AND to the action that was attempted.
 *
 * `username` is null on remove: standing a head down has no target account, so
 * no message may name one.
 */
function appointmentFailure(
  e: unknown,
  teamName: string,
  username: string | null,
  actor: string,
  fallback: string,
  action: AppointmentAction,
): string {
  if (!(e instanceof ApiError)) return fallback;
  const removing = action === 'remove';

  // ── The four codes BOTH endpoints can return ────────────────────────────────
  switch (e.code) {
    // The two-layer gate, reported as two different sentences because they are
    // two different missing things and only one of them is fixable from here.
    case 'not_an_appointer':
      return `Only the Council President or Vice President may ${removing ? 'remove' : 'appoint'} a head moderator, and ${actor} holds neither office. Seat one from the server console: manage.py seat_council_office ${actor} president`;
    case 'not_found':
      // NOT "a team is dissolved when its case resolves" — dissolving does not
      // delete. `dissolve_team` sets `dissolved_at` and leaves the row, the
      // endpoint then answers `team_dissolved`, and nothing in this codebase
      // deletes a CouncilTeam at all. Explaining a 404 with the one mechanism
      // that cannot produce it sends the operator to refresh and find the team
      // still sitting there.
      return removing
        ? `The server has no team matching ${teamName}. Teams are never deleted (even a finished one stays listed), so this is a stale or altered id, not a case that ended. Reload the console.`
        : `The server has no team matching ${teamName}, or no account named ${username}. Teams are never deleted, so reload the console and check the account still exists.`;
    case 'team_dissolved':
      return `${teamName} was dissolved when its case resolved. It never votes again, and its head stays as the record of who led it, so ${removing ? 'that head cannot be stood down' : 'no new head can be seated'}.`;
    case 'case_closed':
      // The old wording ("appointment changes nothing now") described a silent
      // no-op. It is a 400 refusal — the server rejects the change, it does not
      // accept it and shrug.
      return `${teamName}’s case is already decided, and the server refuses head-moderator changes on a decided case. The ${removing ? 'removal' : 'appointment'} was rejected, not quietly ignored.`;
  }

  // ── Grant-only codes ───────────────────────────────────────────────────────
  // The revoke endpoint has no target account and never evaluates the
  // per-candidate ladder, so none of these can reach it. Answering them with a
  // sentence about appointing somebody would be inventing an event.
  if (!removing) {
    const who = username ?? 'that member';
    switch (e.code) {
      case 'self_action':
        return `You cannot appoint yourself head of ${teamName}: nobody takes charge of the case that judges someone on their own say-so. The other appointer (President or Vice President) can seat you.`;
      case 'target_not_active':
        return `${who} is suspended or banned, so they cannot lead ${teamName}. Lift the sanction first.`;
      case 'not_on_team':
        return `${who} is not on ${teamName}. A head moderator leads the team they sit on.`;
      case 'not_a_moderator':
        return `${who} is no longer a Council moderator. Restore their seat from User Roles, or appoint somebody else on ${teamName}.`;
      case 'subject_of_case':
        return `${teamName} is judging a case about ${who}, and the member a case is about cannot be appointed to lead it.`;
      case 'head_already_appointed': {
        const head = typeof e.fields?.head === 'string' ? e.fields.head : 'somebody else';
        return `${teamName} is already led by ${head}. Remove that appointment first. Handover is an explicit revoke-then-grant.`;
      }
    }
  }

  // The bare DRF denial from IsSuperuser: a body of {"detail": …} with no
  // `code`, which apiClient turns into 'http_error'. Reported as itself rather
  // than as a generic failure — it is the OTHER half of the gate
  // `not_an_appointer` reports, and confusing the two sends the operator to seat
  // an office they may already hold.
  if (e.status === 403) {
    return `${removing ? 'Removing' : 'Appointing'} a head moderator is superuser-only, and this account is not a superuser. The Council office alone is not enough.`;
  }
  return fallback;
}

/**
 * Turn a refused topic-refiner write into the sentence that names the fix.
 * `not_configured` is the server refusing to flip (or run) a switch that has
 * nothing to reach: ANTHROPIC_API_KEY is blank on the web/worker containers.
 * A bare 403 is the IsSuperuser gate — staff can read the panel, only the
 * superuser may spend money.
 */
export const REFINER_NOT_CONFIGURED_MESSAGE =
  'Add ANTHROPIC_API_KEY to the server before turning this on.';

/**
 * What to say after "Run one batch now" — and, more to the point, what NOT to
 * say. `POST run-topic-refiner` answers the aggregate plus `ran`, and there are
 * two ways for `lastRun` in that answer to be a PREVIOUS run's row:
 *
 *   • the switch is off (the shipping default) — the sweep returns immediately
 *     and `ran` is false;
 *   • the switch is on but nothing was waiting — the sweep returns BEFORE
 *     writing a run row, so `ran` is true while `lastRun` is still the old one.
 *
 * Quoting `lastRun` in either case reports another batch's numbers ("9 refined
 * of 12") for work that never happened. The second case has no flag of its own,
 * so the caller passes the candidate count it was showing BEFORE the click —
 * the panel already has it, and a run that had nothing to chew on cannot have
 * chewed anything.
 */
export function topicRefinerRunMessage(
  r: { ran?: boolean; lastRun: TopicRefinerRun | null } | null | undefined,
  pendingBefore: number,
): string {
  if (r?.ran === false) {
    return 'Nothing ran: the topic refiner is switched off. Turn it on first (it spends money per batch).';
  }
  if (pendingBefore <= 0) {
    return 'Nothing was waiting to refine. No posts were processed.';
  }
  const last = r?.lastRun;
  return last
    ? `Batch done: ${last.refined} refined, ${last.failed} failed of ${last.candidates}`
    : 'Batch done: no run row was written.';
}

/**
 * The link-preview switch has no `not_configured` case — there is nothing to
 * configure, the fetcher either runs or it doesn't. The one refusal worth its
 * own sentence is the bare 403: staff can READ the panel, but stopping the
 * platform from reaching third-party sites is superuser-only, and "Failed to
 * pause link previews" would send a staff member hunting for a broken button.
 */
function linkPreviewFailure(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.status === 403) {
    return 'Pausing link previews changes what the whole platform fetches, so this switch is superuser-only. This account is not a superuser.';
  }
  return fallback;
}

function refinerFailure(e: unknown, fallback: string): string {
  if (e instanceof ApiError && e.code === 'not_configured') return REFINER_NOT_CONFIGURED_MESSAGE;
  if (e instanceof ApiError && e.status === 403) {
    return 'The topic refiner spends money, so its switch is superuser-only. This account is not a superuser.';
  }
  return fallback;
}

export function useOwnerAction() {
  const store = useOwnerStore();
  const { currentUser } = useSession();
  const show = useToast();
  const actor = currentUser?.id ?? 'owner';

  return {
    suspend: async (username: string, category: ModerationCategory, note?: string) => {
      try {
        await ownerService.suspend(username, category, note);
        show(`Suspended ${username} (${category})`);
      } catch {
        show(`Failed to suspend ${username}`);
      }
    },

    unsuspend: async (username: string) => {
      try {
        await ownerService.unsuspend(username);
        show(`Lifted suspension for ${username}`);
      } catch {
        show(`Failed to unsuspend ${username}`);
      }
    },

    ban: async (username: string, category: ModerationCategory, note?: string) => {
      try {
        await ownerService.ban(username, category, note);
        show(`Banned ${username} (${category})`);
      } catch {
        show(`Failed to ban ${username}`);
      }
    },

    unban: async (username: string) => {
      try {
        await ownerService.unban(username);
        show(`Unbanned ${username}`);
      } catch {
        show(`Failed to unban ${username}`);
      }
    },

    setVerified: async (username: string, value: boolean) => {
      try {
        await ownerService.setVerified(username, value);
        show(value ? `Verified ${username}` : `Removed verification for ${username}`);
      } catch {
        show(`Failed to update verification for ${username}`);
      }
    },

    setStaff: async (username: string, value: boolean) => {
      try {
        await ownerService.setStaff(username, value);
        show(value ? `Granted staff to ${username}` : `Revoked staff from ${username}`);
      } catch {
        show(`Failed to update staff role for ${username}`);
      }
    },

    setCouncilModerator: async (username: string, value: boolean) => {
      try {
        await ownerService.setCouncilModerator(username, value);
        show(
          value
            ? `Seated ${username} on the Council`
            : `Removed ${username} from the Council`,
        );
      } catch {
        show(`Failed to update Council seat for ${username}`);
      }
    },

    setCouncilPresident: async (username: string, value: boolean) => {
      try {
        await ownerService.setCouncilPresident(username, value);
        show(
          value
            ? `Seated ${username} as Council President`
            : `Removed ${username} as Council President`,
        );
      } catch (e) {
        show(
          officeFailure(
            e,
            username,
            'President',
            'president',
            `Failed to update Council presidency for ${username}`,
          ),
        );
      }
    },

    setCouncilVicePresident: async (username: string, value: boolean) => {
      try {
        await ownerService.setCouncilVicePresident(username, value);
        show(
          value
            ? `Seated ${username} as Vice President`
            : `Removed ${username} as Vice President`,
        );
      } catch (e) {
        show(
          officeFailure(
            e,
            username,
            'Vice President',
            'vice-president',
            `Failed to update Council vice presidency for ${username}`,
          ),
        );
      }
    },

    setCouncilSenator: async (username: string, value: boolean) => {
      try {
        await ownerService.setCouncilSenator(username, value);
        show(
          value ? `Seated ${username} in the Senate` : `Removed ${username} from the Senate`,
        );
      } catch (e) {
        show(
          officeFailure(
            e,
            username,
            'Senator',
            'senator',
            `Failed to update Senate seat for ${username}`,
          ),
        );
      }
    },

    // Head-moderator appointment. `teamName` is passed alongside `teamId`
    // purely so the messages can name the team the operator is looking at —
    // `teamId` is a random slug (`team-9f3a...`) and naming it would be worse
    // than naming nothing.
    appointHeadModerator: async (teamId: string, teamName: string, username: string) => {
      try {
        await ownerService.appointHeadModerator(teamId, username);
        show(`Appointed ${username} head moderator of ${teamName}`);
      } catch (e) {
        show(
          appointmentFailure(
            e,
            teamName,
            username,
            actor,
            `Failed to appoint ${username} as head moderator of ${teamName}`,
            'appoint',
          ),
        );
      }
    },

    revokeHeadModerator: async (teamId: string, teamName: string) => {
      try {
        await ownerService.revokeHeadModerator(teamId);
        show(`Removed the head moderator of ${teamName}`);
      } catch (e) {
        show(
          appointmentFailure(
            e,
            teamName,
            null,
            actor,
            `Failed to remove the head moderator of ${teamName}`,
            'remove',
          ),
        );
      }
    },

    removePost: async (postId: string, category: ModerationCategory, note?: string) => {
      try {
        await ownerService.removePost(postId, category, note);
        show(`Removed post ${postId} (${category})`);
      } catch {
        show(`Failed to remove post ${postId}`);
      }
    },

    restorePost: async (postId: string) => {
      try {
        await ownerService.restorePost(postId);
        show(`Restored post ${postId}`);
      } catch {
        show(`Failed to restore post ${postId}`);
      }
    },

    // ── Topic refiner switch (1.24.00 B4) ─────────────────────────────────────
    // Each resolves true on success so the section can refetch the aggregate.
    // `not_configured` (409) is the one refusal worth a sentence: the switch
    // won't flip without ANTHROPIC_API_KEY on the server, and "Failed" would
    // send the owner hunting for a bug that is a missing env var.
    enableTopicRefiner: async () => {
      try {
        await ownerService.enableTopicRefiner();
        show('Topic refiner turned on');
        return true;
      } catch (e) {
        show(refinerFailure(e, 'Failed to turn the topic refiner on'));
        return false;
      }
    },

    disableTopicRefiner: async () => {
      try {
        await ownerService.disableTopicRefiner();
        show('Topic refiner turned off');
        return true;
      } catch (e) {
        show(refinerFailure(e, 'Failed to turn the topic refiner off'));
        return false;
      }
    },

    /**
     * `pendingBefore` is the "Candidates waiting" count the panel was showing
     * when the button was pressed — see topicRefinerRunMessage for why the
     * response alone cannot tell an idle run from a real one.
     */
    runTopicRefiner: async (pendingBefore: number) => {
      try {
        const r = await ownerService.runTopicRefiner();
        show(topicRefinerRunMessage(r, pendingBefore));
        return true;
      } catch (e) {
        show(refinerFailure(e, 'Failed to run a topic-refiner batch'));
        return false;
      }
    },

    // ── Link-preview kill switch (1.24.00 F1) ─────────────────────────────────
    // Each resolves true on success so the section can refetch the aggregate.
    // There is no `not_configured` refusal here — only the IsSuperuser gate,
    // which linkPreviewFailure names.
    pauseLinkPreviews: async () => {
      try {
        await ownerService.pauseLinkPreviews();
        show('Link previews paused. No new links will be fetched');
        return true;
      } catch (e) {
        show(linkPreviewFailure(e, 'Failed to pause link previews'));
        return false;
      }
    },

    resumeLinkPreviews: async () => {
      try {
        await ownerService.resumeLinkPreviews();
        show('Link previews resumed');
        return true;
      } catch (e) {
        show(linkPreviewFailure(e, 'Failed to resume link previews'));
        return false;
      }
    },

    /** Still-prototype infra actions (clear-cache, announcements, etc.) — client-only log. */
    logAction: (action: string, target: string, note?: string) => {
      store.logAction({ action, target, actor, note });
      show(`${action}: ${target}`);
    },
  };
}
